import { useRouter } from 'next/router';
import React, {
  useContext,
  useState,
  createContext,
  useEffect,
  useRef,

} from 'react';
import { toast } from 'react-toastify';
import { IItemPageDataStoredItem, db, App } from '@typedream/data';
import TokenContext from '../../utils/jwt/tokenContext';
import {
  ICollectionColumn,
  ICollectionDataResultItem,
  ICollectionUpdateRowRawRequest,
  IPage,
} from '../../types';
import { errToString } from '../../utils/errors';
import { NETWORK_ERROR } from '../../constants';
import { useSpaceAndPageIDFromRouter } from '../space/hooks';
import { useGetPageQuery, useUpdateCollectionItemMutation } from '../../store';
import { api } from '../api';
/**
 * Contains the authenticated state and authentication methods provided by the `usePages` hook.
 */
export interface ItemPageDataContextInterface {
  dataID: string, // (optional) for item page template
  loadingSave: boolean,
  loadingPublish: boolean,
  saveData: (
    dataID: string,
    pageID: string,
    spaceID: string,
    newItemData: { [key: string]: string },
  ) => Promise<void>;
  getItemPageDataForPage: (
    _spaceID: string,
    _page: IPage,
    _dataID: string,
  ) => Promise<ICollectionDataResultItem>;
  itemData: ICollectionDataResultItem;
  columns: { [key: string]: ICollectionColumn };
  hasUnsavedChanges: boolean,
  sethasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>
  isRTCOpen: boolean;
  setisRTCOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isNotionOpen: boolean;
  setisNotionOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const stub = (): never => {
  throw new Error('You forgot to wrap your component in <ItemPageDataProvider>.');
};
/**
 * The initial initialContext.
 */
export const initialContext: ItemPageDataContextInterface = {
  dataID: null,
  itemData: null,
  saveData: stub,
  getItemPageDataForPage: stub,
  loadingSave: false,
  columns: null,
  loadingPublish: false,
  hasUnsavedChanges: false,
  sethasUnsavedChanges: stub,
  isRTCOpen: false,
  setisRTCOpen: null,
  isNotionOpen: false,
  setisNotionOpen: null,
};

/**
 * The Pages Context
 */
export const ItemPageDataContext = createContext<ItemPageDataContextInterface>(
  initialContext,
);

/**
 * ```jsx
 * <TokenContext>
 *   <ItemPageDataProvider>
 *     <MyApp />
 *   </ItemPageDataProvider>
 * </TokenContext>
 * ```
 *
 * Provides the ItemPageDataContext to its child components.
 */
export function ItemPageDataProvider({ children }) {
  const [_columns, setcolumns] = useState({});
  const [loadingSave, setloadingSave] = useState(false);
  const [loadingPublish, setloadingPublish] = useState(false);
  const [isRTCOpen, setisRTCOpen] = useState(false);
  const [isNotionOpen, setisNotionOpen] = useState(false);
  const [itemData, setitemData] = useState<ICollectionDataResultItem>(null);
  const [hasUnsavedChanges, sethasUnsavedChanges] = useState(false);
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const { data: page } = useGetPageQuery([spaceID, pageID]);
  const { spacePermissionTokenHandler, subscriptionTokenHandler } = useContext(
    TokenContext,
  );
  const router = useRouter();
  const { data_id } = router.query;

  const columnsRef = useRef(_columns);

  useEffect(() => {
    columnsRef.current = _columns;
  }, [_columns]);

  const listColumns = async (): Promise<{ [key: string]: ICollectionColumn }> => {
    const itemPageData = page?.customization?.item_page_data;
    try {
      const cols = await new App(api().api).app(itemPageData)?.getColumnNames(
        spaceID,
        itemPageData,
        spacePermissionTokenHandler,
        subscriptionTokenHandler,
      );
      setcolumns(cols);
      return cols;
    } catch (e) {
      toast.error(errToString(e));
    }
    return null;
  };

  useEffect(() => {
    if (page?.customization?.item_page_data) {
      listColumns();
    }
  }, [page?.customization?.item_page_data]);

  const getItemPageDataForPage = async (
    _spaceID: string,
    _page: IPage,
    _dataID: string,
  ): Promise<ICollectionDataResultItem> => {
    if (_page) {
      const itemPageData = _page?.customization?.item_page_data;
      const app = new App(api().api);
      if (itemPageData && app.app(itemPageData)?.tableConnected(itemPageData)) {
        const dataResult = await app.app(itemPageData)?.getDataByID(
          _spaceID,
          _dataID,
          itemPageData,
          spacePermissionTokenHandler,
          subscriptionTokenHandler,
        );

        setitemData(dataResult);
        return dataResult;
      }
    }
    return null;
  };

  const saveDataLocal = async (
    dataID: string,
    _pageID: string,
    _spaceID: string,
    dataName: string,
    newItemData: any, // this is the whole itemData
  ) => {
    const itemPageToSave: IItemPageDataStoredItem = {
      data_id: dataID,
      page_id: _pageID,
      content: newItemData,
      space_id: _spaceID,
      name: dataName,
      saved_on: new Date(),
    };
    await db.itemData.put(itemPageToSave, dataID);
  };

  const { mutate: updateCollectionItem } = useUpdateCollectionItemMutation();

  const saveData = async (
    dataID: string,
    _pageID: string,
    _spaceID: string,
    newItemData: { [columnName: string]: string },
  ) => {
    setloadingSave(true);

    const itemPageData = page?.customization?.item_page_data;

    if (!columnsRef.current || !newItemData) {
      toast.error("There's no data to save or your columns hasn't loaded yet");
      setloadingSave(false);
      return;
    }

    try {
      const rawRowUpdates: ICollectionUpdateRowRawRequest[] = Object.entries(newItemData).filter(
        ([, value]) => value !== null && value !== undefined && value !== '',
      ).map(
        ([key, value]) => ({
          column: columnsRef.current[key],
          value,
        }),
      );

      const dataResult = await updateCollectionItem(_spaceID, dataID, rawRowUpdates, itemPageData);

      setitemData(dataResult?.data || null);
      setloadingSave(false);
    } catch (e) {
      if (e.message === NETWORK_ERROR) {
        await saveDataLocal(
          dataID,
          _pageID,
          _spaceID,
          newItemData?.[itemPageData?.pageSchema?.name],
          newItemData,
        );
      }
      toast.error(errToString(e));
      setloadingSave(false);
    }
  };

  return (
    <ItemPageDataContext.Provider
      value={{
        dataID: data_id as string,
        saveData,
        getItemPageDataForPage,
        itemData,
        loadingSave,
        columns: _columns,
        loadingPublish,
        hasUnsavedChanges,
        sethasUnsavedChanges,
        isRTCOpen,
        setisRTCOpen,
        isNotionOpen,
        setisNotionOpen,
      }}
    >
      {children}
    </ItemPageDataContext.Provider>
  );
}
